import { FC, useState, useImperativeHandle, forwardRef } from 'react'
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Box,
  Badge,
  IconButton,
} from '@mui/material'
import {
  ExpandMore,
  MoreVert,
  Notifications as NotificationsIcon,
  Add as AddIcon,
} from '@mui/icons-material'
import { Surgery } from 'common/types'
import { ProcedureListTableProps } from './ProcedureList.types'
import { useProcedureListTableLogic } from './ProcedureListTable.logic'
import EventStatus from './EventStatus'
import { useFilters } from 'lib/hooks/useFilters'
import { useSurgeryGrouping, useHospitalExpansion } from './hooks'
import './GroupedProcedureListTable.scss'
import BellIcon from 'assets/icons/BellIcon'
import specialRequestIcon from 'assets/icons/images/specialRequestIcon.png'
import successIcon from 'assets/icons/images/success.png'
import { CustomModal } from 'components/molecules/Modal/Modal'
import Notifications from 'views/Notifications/Notifications'
import { SpecialRequestModal } from './SpecialRequestModal/SpecialRequestModal'
import { SpecialRequestInfoModal } from './SpecialRequestInfoModal/SpecialRequestInfoModal'
import EditProcedureModal from './EditProcedureModal/EditProcedureModal'
import { CircularProgress } from '@mui/material'
import { SurgeryTraysDisplay, useSurgeryTrays } from './SurgeryTraysDisplay'
import classNames from 'classnames'

// Special Request Icon Component
const SpecialRequestIcon: FC<{
  success?: boolean
  qty?: number
  surgeryId: string
}> = ({ success = false, qty = 0 }) => (
  <div
    className="special-request-icon-wrapper"
    style={{ position: 'relative' }}
  >
    <img
      src={specialRequestIcon}
      alt="special-request"
      className="special-request-icon"
    />
    {qty > 0 ? (
      <div className="special-request-qty-wrapper">
        <Typography variant="caption" fontWeight={800} fontSize={10}>
          {qty}
        </Typography>
      </div>
    ) : (
      <div className="add-special-request-wrapper">
        <AddIcon className="add-special-request" />
      </div>
    )}
    {success && <img src={successIcon} alt="success" className="success" />}
  </div>
)

interface HospitalSectionProps {
  hospitalName: string
  surgeries: Surgery[]
  isExpanded: boolean
  onToggle: () => void
  logic: ReturnType<typeof useProcedureListTableLogic>
  onTrayAssignmentChange?: () => void
  beforeDate: Date | undefined
  afterDate: Date | undefined
  expandTraysForSurgeryId?: string | null
}

// Surgery Card Component with Tray Display
const SurgeryCard: FC<{
  surgery: Surgery
  logic: ReturnType<typeof useProcedureListTableLogic>
  expanded: boolean
  onToggleExpanded: () => void
  onTrayAssignmentChange?: () => void
  beforeDate?: Date
  afterDate?: Date
  isLastViewedSurgery?: boolean
}> = ({
  surgery,
  logic,
  expanded,
  onToggleExpanded,
  onTrayAssignmentChange,
  beforeDate,
  afterDate,
  isLastViewedSurgery,
}) => {
  const {
    filteredTrays,
    loading: traysLoading,
    error: traysError,
    updateTrays,
    refetchTrays,
  } = useSurgeryTrays(surgery._id)
  const hasTrays = filteredTrays.length > 0

  // Handle tray assignment changes
  const handleTrayAssignmentChange = () => {
    // Refetch this surgery's trays
    refetchTrays()
    // Also trigger the parent refetch for ProceduresNurse
    onTrayAssignmentChange?.()
  }

  return (
    <div
      className={classNames('surgery-card-container', {
        'has-trays': hasTrays,
        'highlight-surgery': isLastViewedSurgery,
      })}
      id={surgery._id}
      data-testid={`test-procedure-${surgery._id}`}
      data-surgery-id={surgery._id}
    >
      <Box
        key={surgery._id}
        className={classNames('surgery-card', hasTrays && 'has-trays')}
        onClick={() => logic.handleStartSurgery(surgery)}
      >
        <Box className="surgery-case-info">
          {logic.caseInformation({
            surgery: surgery,
            isNurse: logic.isNurse || false,
          })}
        </Box>

        <Box className="surgery-event-status">
          <Box className="icons-wrapper">
            {/* Options Menu */}
            {logic.flags.enableEditSurgery &&
              surgery.status.name !== 'SUBMITTED' && (
                <IconButton
                  className="more-vert-icon"
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation()
                    logic.handleOpenOptionsModal(surgery)
                  }}
                >
                  <MoreVert />
                </IconButton>
              )}

            {/* Notifications Button */}
            {logic.isRep ? null : logic.flags.caseHomeNotificationsButton ? (
              <IconButton
                className="bell-icon"
                size="small"
                onClick={(e) => {
                  e.stopPropagation()
                  if (!logic.hasNotifications(surgery)) return
                  logic.setIsNotificationsOpen({
                    isOpen: true,
                    surgery,
                  })
                }}
              >
                {logic.hasNotifications(surgery) ? (
                  <BellIcon />
                ) : (
                  <NotificationsIcon color="disabled" />
                )}
              </IconButton>
            ) : null}

            {/* Special Request Button */}
            {logic.enableSpecialRequest &&
              !logic.isRep &&
              (surgery.status.name === 'SCANNING' ||
                (surgery.status.name === 'SUBMITTED' &&
                  surgery.specialRequests &&
                  surgery.specialRequests.length > 0)) && (
                <IconButton
                  id={
                    surgery.specialRequests &&
                    surgery.specialRequests.length > 0
                      ? 'showSpecialRequests'
                      : 'addSpecialRequest'
                  }
                  size="small"
                  onClick={(e) =>
                    logic.handleSpecialRequest(
                      e,
                      surgery._id,
                      surgery.status.name
                    )
                  }
                >
                  <SpecialRequestIcon
                    qty={
                      surgery.specialRequests
                        ? surgery.specialRequests.length
                        : 0
                    }
                    surgeryId={surgery._id}
                  />
                </IconButton>
              )}
          </Box>

          <EventStatus
            isStarted={surgery.isStarted}
            isTestProcedure={surgery?.isTestProcedure}
            isScans={surgery?.assetGroups.length !== 0}
            surgeryStatus={surgery?.status.name}
            hasUndocumentedScans={surgery?.undocumentedScansCount > 0}
            implantReportStatus={surgery?.implantReportStatus}
            addendumStatus={surgery?.addendums?.length > 0}
          />
        </Box>
      </Box>

      {hasTrays && (
        <Box onClick={(e) => e.stopPropagation()}>
          <SurgeryTraysDisplay
            surgeryId={surgery._id}
            trays={filteredTrays}
            loading={traysLoading}
            error={traysError}
            expanded={!expanded}
            onToggleExpanded={onToggleExpanded}
            onTraysUpdate={updateTrays}
            onTrayAssignmentChange={handleTrayAssignmentChange}
            beforeDate={beforeDate ? beforeDate.toISOString() : undefined}
            afterDate={afterDate ? afterDate.toISOString() : undefined}
          />
        </Box>
      )}
    </div>
  )
}

const HospitalSection: FC<HospitalSectionProps> = ({
  hospitalName,
  surgeries,
  isExpanded,
  onToggle,
  logic,
  onTrayAssignmentChange,
  beforeDate,
  afterDate,
  expandTraysForSurgeryId,
}) => {
  // Per-case expand/collapse state for trays
  const [expandedTrays, setExpandedTrays] = useState<{
    [surgeryId: string]: boolean
  }>({})

  return (
    <Accordion
      className="hospital-accordion"
      expanded={isExpanded}
      onChange={onToggle}
    >
      <AccordionSummary
        expandIcon={<ExpandMore />}
        className="hospital-accordion-summary"
      >
        <Box className="hospital-header">
          <Box className="hospital-icon-container">
            <Typography color="var(--primary)" variant="h2">
              {hospitalName.charAt(0)}
            </Typography>
          </Box>
          <Box className="hospital-info">
            <Typography variant="h6" className="hospital-name">
              {hospitalName} Hospital
            </Typography>
            <Typography variant="body2" className="surgery-count">
              {surgeries.length} {surgeries.length === 1 ? 'case' : 'cases'}
            </Typography>
          </Box>
          <Badge
            badgeContent={surgeries.length}
            color="primary"
            className="hospital-badge"
          />
        </Box>
      </AccordionSummary>
      <AccordionDetails className="hospital-accordion-details">
        <Box className="surgeries-container">
          {surgeries.map((surgery: Surgery) => (
            <SurgeryCard
              key={surgery._id}
              surgery={surgery}
              logic={logic}
              expanded={!!expandedTrays[surgery._id]}
              onToggleExpanded={() =>
                setExpandedTrays((prev) => ({
                  ...prev,
                  [surgery._id]: !prev[surgery._id],
                }))
              }
              onTrayAssignmentChange={onTrayAssignmentChange}
              beforeDate={beforeDate}
              afterDate={afterDate}
              isLastViewedSurgery={expandTraysForSurgeryId === surgery._id}
            />
          ))}
        </Box>
      </AccordionDetails>
    </Accordion>
  )
}

export interface GroupedProcedureListTableRef {
  toggleHospital: (hospitalName: string) => void
}

export const GroupedProcedureListTable = forwardRef<
  GroupedProcedureListTableRef,
  ProcedureListTableProps & { expandTraysForSurgeryId?: string | null }
>(
  (
    {
      events,
      loading,
      beforeDate,
      afterDate,
      expandTraysForSurgeryId,
      ...rest
    },
    ref
  ) => {
    const logic = useProcedureListTableLogic({
      ...rest,
      beforeDate,
      afterDate,
      events,
      loading,
    })
    const [filters] = useFilters()

    // Group surgeries by hospital with filtering
    const { groupedSurgeries, hospitalNames, hasFilteredResults } =
      useSurgeryGrouping({
        surgeries: logic.sortedSurgeries || [],
        filters,
      })

    // Manage hospital expansion state
    const { expandedHospitals, toggleHospital } = useHospitalExpansion({
      filters,
      hospitalNames,
    })

    // Expose toggleHospital function to parent component
    useImperativeHandle(
      ref,
      () => ({
        toggleHospital,
      }),
      [toggleHospital]
    )

    if (loading) {
      return (
        <Box className="grouped-procedure-list-loading">
          <Typography>Loading your case log...</Typography>
        </Box>
      )
    }

    if (!logic.hasItems || !hasFilteredResults) {
      return (
        <Box className="grouped-procedure-list-empty">
          <Typography variant="subtitle2">
            There are no procedures currently scheduled that match your filter
            criteria
          </Typography>
        </Box>
      )
    }

    return (
      <Box className="grouped-procedure-list-container">
        {hospitalNames.map((hospitalName) => {
          const surgeries = groupedSurgeries.get(hospitalName) || []
          return (
            <HospitalSection
              key={hospitalName}
              hospitalName={hospitalName}
              surgeries={surgeries}
              isExpanded={expandedHospitals.has(hospitalName)}
              onToggle={() => toggleHospital(hospitalName)}
              logic={logic}
              onTrayAssignmentChange={rest.onTrayAssignmentChange}
              beforeDate={beforeDate}
              afterDate={afterDate}
              expandTraysForSurgeryId={expandTraysForSurgeryId}
            />
          )
        })}

        {/* Notifications Modal */}
        <CustomModal
          fitContentWidth={logic.isMobile}
          open={logic.isNotificationsOpen.isOpen}
          header="Notifications"
          isLoadingState={false}
          secondaryButtonAction={() =>
            logic.setIsNotificationsOpen({ isOpen: false })
          }
          primaryButtonDisabled
          closeButtonOnly
        >
          <Notifications
            surgery={logic.isNotificationsOpen?.surgery as Surgery}
            isMobile={logic.isMobile}
          />
        </CustomModal>

        {/* Special Request Modals */}
        {logic.showSpecialRequestModal.showModal === 'add' && (
          <SpecialRequestModal
            surgeryId={logic.showSpecialRequestModal.surgeryId as string}
            onClose={logic.handleCloseSpecialRequest}
            beforeDate={beforeDate}
            afterDate={afterDate}
          />
        )}

        {logic.loadingSpecialRequests && (
          <div className="loader">
            <CircularProgress />
          </div>
        )}

        {logic.showSpecialRequestModal.showModal === 'show' && (
          <SpecialRequestInfoModal
            canAdd={logic.showSpecialRequestModal.surgeryStatus === 'SCANNING'}
            showModal={!logic.loadingSpecialRequests}
            surgeryId={logic.showSpecialRequestModal.surgeryId as string}
            onClose={logic.handleCloseSpecialRequest}
            onHeaderButtonAction={(surgeryId) => {
              logic.handleCloseSpecialRequest()
              logic.setShowSpecialRequestModal((prev) => ({
                ...prev,
                showModal: 'add',
                surgeryId,
              }))
            }}
            onFinishLoading={logic.setLoadingSpecialRequests}
          />
        )}

        {/* Edit Procedure Modal */}
        {logic.editModalSurgery && (
          <EditProcedureModal
            open={!!logic.editModalSurgery}
            onClose={logic.handleCloseEditModal}
            surgery={logic.editModalSurgery}
            beforeDate={beforeDate}
            afterDate={afterDate}
          />
        )}
      </Box>
    )
  }
)
